<template>
  <div class="projet-part">
    <div class="presentation-container">
      <div id="aide-labellissation" class="presentation-explanations">
        <h3
          v-if="
            projetAnnuel.typeDossier == 'DOSSIER_LABELLISATION' ||
            projetAnnuel.typeDossier == 'DOSSIER_INSCRIPTION'
          "
        >
          {{ $t("projet.labellise.presentation") }}
        </h3>
        <h3
          v-if="
            projetAnnuel.typeDossier == 'DOSSIER_RENOUVELLEMENT' ||
            projetAnnuel.typeDossier == 'DOSSIER_RENOUVELLEMENT_LIGHT'
          "
        >
          {{ $t("projet.labellise.renewal") }}
        </h3>

        <!-- eslint-disable vue/no-v-html -->
        <div class="explanation-bloc">
          <div class="explanation-bloc-text">
            <div class="toggle-validation">
              <div class="toggle-validation-text">
                <span
                  v-if="!validationActivated"
                  v-html="$t('projet.labellise.recap.help-on')"
                ></span>
                <span v-else v-html="$t('projet.labellise.recap.help-off')"></span>
              </div>
              <hr />
              <b-switch
                v-model="validationActivatedInternal"
                @update:model-value="$emit('toggle-validation')"
              />
            </div>
          </div>
        </div>

        <!-- Warnings & Errors recap -->
        <div v-if="validationActivated" class="presentation-recap">
          <RecapCard>
            <div v-if="errorsPerLabel.size">
              <span class="invalid-text">{{ $t("projet.labellise.invalid") }}</span>
              <ul>
                <li
                  v-for="section in errorsPerLabel.keys()"
                  :key="section"
                  class="section-in-error"
                >
                  <button class="section-errors-link" @click="redirectToSection(section)">
                    <b-icon size="is-normal" class="item-icon" :icon="getSectionIcon(section)" />
                    <span
                      v-if="!isPorteurProjet && (section == 'mon-annee' || section == 'labellise')"
                    >
                      {{ $t("projet.menu." + section + "-accompagnateur") }}</span
                    >
                    <span v-else>{{ $t("projet.menu." + section) }}</span>
                  </button>
                  <ul>
                    <li
                      v-for="fieldWithError in Array.from(getSectionErrors(section).keys()).sort(
                        compareSections,
                      )"
                      :key="fieldWithError"
                      class="field-in-error"
                    >
                      <span class="error-circle">
                        <span>{{ getSectionErrors(section).get(fieldWithError) }}</span>
                      </span>
                      <span v-if="section != fieldWithError">
                        {{ $t("projet." + section + "." + fieldWithError) }}
                      </span>
                      <span v-else>
                        {{ $t("projet.labellise.section-invalid") }}
                      </span>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
            <div v-else>
              {{ $t("projet.labellise.all-valid") }}
            </div>
          </RecapCard>
        </div>

        <div class="explanation-bloc">
          <div class="explanation-bloc-pic">
            <b-icon type="is-success" size="is-large" icon="exclamation-circle" />
          </div>
          <div class="explanation-bloc-text">
            <strong>{{ $t(`projet.labellise.recap.why-title${renewalSuffix}`) }} </strong>
            <p v-html="$t('projet.labellise.recap.why-text')"></p>
          </div>
        </div>
        <div class="explanation-bloc">
          <div class="explanation-bloc-pic">
            <b-icon type="is-success" size="is-large" icon="smile" />
          </div>
          <div class="explanation-bloc-text">
            <strong>{{ $t("projet.labellise.recap.how-title") }} </strong>
            <p v-html="$t('projet.labellise.recap.how-text')"></p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue"
import RecapCard from "@/views/projects_pp/RecapCard.vue"

import { ProjetAnnualValidation } from "@/pages/projet-page/commons/ProjectValidationAndSaveHandler.vue"
import { ProjetAnnuelDTO } from "@/model/bean/GeneratedDTOs"
import { BIcon, BSwitch } from "buefy"

export default defineComponent({
  name: "LabellisationRecap",
  components: {
    RecapCard,
    BIcon,
    BSwitch,
  },
  props: {
    projetValidation: {
      type: Object as PropType<ProjetAnnualValidation>,
      required: false,
      default: () => new ProjetAnnualValidation(),
    },
    projetAnnuel: {
      type: Object as PropType<ProjetAnnuelDTO>,
      required: true,
    },
    validationActivated: {
      type: Boolean,
      default: true,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    sectionToAnchors: {
      type: Map,
      required: false,
      default: () => new Map<string, string[]>(),
    },
    sectionsToIcon: {
      type: Map,
      required: false,
      default: () => new Map<string, string>(),
    },
    renewalSuffix: {
      type: String,
      default: "",
    },
    isPorteurProjet: {
      type: Boolean,
      required: false,
    },
  },

  emits: ["toggle-validation"],
  data() {
    return {
      errorsPerLabel: new Map<string, Map<string, number>>(),
      validationActivatedInternal: false,
    }
  },
  computed: {
    // Computed property pour obtenir les erreurs d'une section de manière sécurisée
    getSectionErrors() {
      return (section: string): Map<string, number> => {
        return this.errorsPerLabel.get(section) || new Map<string, number>()
      }
    },

    // Computed property pour obtenir l'icône d'une section de manière sécurisée
    getSectionIcon() {
      return (section: string): string => {
        const icon = this.sectionsToIcon.get(section)
        return typeof icon === "string" ? icon : "question-circle"
      }
    },
  },
  watch: {
    validationActivated() {
      this.validationActivatedChanged()
    },
    projetValidation() {
      this.validationChanged()
    },
  },
  mounted(): void {
    this.validationActivatedChanged()
  },
  methods: {
    validationActivatedChanged(): void {
      this.validationActivatedInternal = this.validationActivated
    },

    validationChanged(): void {
      // Sort errors & warnings per sections and subfields
      this.errorsPerLabel = new Map<string, Map<string, number>>()
      const allErrors = Array.from(this.projetValidation.invalidFields.keys())
      const allWarnings = Array.from(this.projetValidation.emptyFields.keys())
      allErrors.forEach((field: string) => {
        if (field != "finalisation") {
          const numberOfError = this.projetValidation.invalidFields.get(field)?.length
          const section = this.getSectionForField(field)
          let existingErrorsForSection = this.errorsPerLabel.get(section)
          if (!existingErrorsForSection) {
            existingErrorsForSection = new Map<string, number>()
          }
          existingErrorsForSection.set(field, numberOfError ? numberOfError : 0)
          this.errorsPerLabel.set(section, existingErrorsForSection)
        }
      })
      allWarnings.forEach((field: string) => {
        if (field != "finalisation") {
          const numberOfWarnings = this.projetValidation.emptyFields.get(field)?.length
          const numberOfError = this.projetValidation.invalidFields.get(field)?.length
          const total =
            (numberOfError ? numberOfError : 0) + (numberOfWarnings ? numberOfWarnings : 0)
          const section = this.getSectionForField(field)
          let existingErrorsForSection = this.errorsPerLabel.get(section)
          if (!existingErrorsForSection) {
            existingErrorsForSection = new Map<string, number>()
          }
          existingErrorsForSection.set(field, total)
          this.errorsPerLabel.set(section, existingErrorsForSection)
        }
      })
    },

    redirectToSection(section: string): void {
      this.$router.push({ params: { section: section } })
    },
    getSectionForField(field: string): string {
      for (const key of this.sectionToAnchors.keys()) {
        const keyString = key as string
        let anchorsForSection = this.sectionToAnchors.get(key) as string[]
        if (!anchorsForSection) {
          anchorsForSection = []
        }
        if (anchorsForSection.indexOf(field) > -1) {
          return keyString
        }
      }
      return field
    },

    compareSections(a: string, b: string): number {
      let result = 0
      if (a == "autresinfos") {
        result = 1000
      } else if (b == "autresinfos") {
        result = -1000
      } else if (a == "diffusion") {
        result = 500
      } else if (b == "diffusion") {
        result = -500
      } else {
        result = a.localeCompare(b)
      }
      return result
    },
  },
})
</script>

<style lang="scss" scoped>
@import "@/styles/variables";

p {
  font-size: 14px;
}

.presentation-container {
  display: block !important;
  overflow-x: auto; // Hides horizontal overflow
  word-wrap: break-word;

  .presentation-explanations {
    padding-right: 20px;
    //padding-top: 50px;
    .explanation-bloc {
      padding-bottom: 30px;
      display: flex;

      .explanation-bloc-pic {
        padding-left: 10px;
        width: 70px;
      }

      .explanation-bloc-text {
        word-spacing: normal; // Resets word spacing to default
        letter-spacing: normal;
        width: calc(38vw - 80px);
        text-align: left;
        //text-justify: distribute;
        box-sizing: border-box;
        //max-width: 100%;
      }
    }

    .toggle-validation {
      border: 2px solid $bookmarkcard-border-color;
      border-radius: 10px;
      margin-top: 15px;

      .toggle-validation-text {
        span {
          display: block;
          align-items: center; // Vertically centers the text inside the span
          justify-content: center;
        }

        padding: 15px;
      }
    }
  }

  .presentation-recap {
    flex-shrink: 0;
    height: auto;

    .recap {
      width: auto;

      .BookmarkCard-card {
        width: auto;
        height: auto;
      }

      .BookmarkCard-content {
        height: auto;
        padding: 20px;
      }

      .BookmarkCard-icon {
        margin-top: 0 !important;
      }

      .section-in-error {
        font-size: 17px;
        padding-bottom: 20px;
      }

      .section-errors-link {
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        text-align: left;
        display: flex;
        align-items: center;
        font-family: inherit;
        font-size: inherit;

        span {
          color: $ofb-bleu-2;
        }

        &:hover {
          span {
            font-weight: bolder;
            color: $ofb-bleu-1;
          }
        }
      }

      .invalid-text {
        width: auto;
        display: block;
      }

      .field-in-error {
        font-size: 15px;
        padding-left: 10px;
        padding-top: 10px;

        a {
          color: black;

          &:hover {
            color: $red-errors;
            font-weight: bolder;
          }
        }

        .error-circle {
          border-radius: 50%;
          display: block;
          width: 22px;
          height: 22px;
          background-color: $red-errors;
          float: left;
          margin-left: 5px;
          margin-right: 5px;

          span {
            color: white;
            font-weight: bolder;
            padding-left: 6px;
            margin-right: 6px;
            font-size: 16px;
          }
        }
      }
    }
  }
}

.projet-part {
  display: flex; // This will lay out its children in a row
  justify-content: center; // This centers the children in the container
  align-items: flex-start; // This aligns children to the top of the container
  padding: 20px; // Add padding as needed
  gap: 20px; // Adds space between flex items
}

.presentation-container {
  display: flex;
  flex-direction: column; // Stack children vertically
  width: 100%; // Adjust the width as needed
  //max-height: 90vh; // Adjust the height as needed
  //overflow-y: auto; // Adds a scrollbar if content overflows
}

.presentation-explanations,
.presentation-recap {
  flex: 1; // Each child will take up equal space
  margin: 0; // Reset margin to ensure alignment
  padding: 10px; // Add padding inside each child as needed
}

// Add a media query for smaller screens if needed
@media (max-width: 768px) {
  .projet-part {
    flex-direction: column;
  }

  .presentation-explanations,
  .presentation-recap {
    flex: none; // On small screens, don't force them to be the same size
    width: 100%; // Take up the full width of the container
  }
}

.toggle-validation {
  display: flex;
  align-items: center; // Center items horizontally
  justify-content: center; // Center items vertically, if there's extra space
}

.b-switch {
  width: 100%; // Optional: if you want the switch to expand to the full width of its parent
}
</style>
